<template>
  <v-card class="brain-simple-card" elevation="1">
    <v-card-text>
      <div class="card-header">
        <v-icon size="22" color="#EA5545" class="mr-2">mdi-brain</v-icon>
        <div>
          <div class="card-title">{{ t('brainComponents.simple.title') }}</div>
          <div class="card-subtitle">
            {{ t('brainComponents.simple.subtitle') }}
          </div>
        </div>
      </div>

      <v-alert
        v-if="!hasProduct"
        type="warning"
        variant="tonal"
        class="mb-4"
      >
        {{ t('brainComponents.simple.noProduct') }}
      </v-alert>

      <v-textarea
        v-model="question"
        :disabled="isSending || isSearchingAsync || !hasProduct"
        variant="outlined"
        :placeholder="t('brainComponents.simple.textareaPlaceholder')"
        rows="3"
        class="mb-4"
        auto-grow
        clearable
        @input="errorText = ''"
      ></v-textarea>

      <div class="actions-row">
        <v-btn
          v-if="!showAsyncButton"
          color="#EA5545"
          variant="flat"
          :disabled="!canSubmit"
          :loading="isSending"
          @click="sendQuestion"
        >
          <v-icon left size="18">mdi-send</v-icon>
          {{ t('brainComponents.simple.actionButton') }} ({{ props.productName }} - Tarif: {{ props.productId || 'N/A' }})
        </v-btn>
        <v-btn
          v-if="showAsyncButton"
          color="#EA5545"
          variant="flat"
          :disabled="!canSubmit || isSearchingAsync"
          :loading="isSearchingAsync"
          @click="sendQuestionAsync"
        >
          <v-icon left size="18">mdi-send</v-icon>
          {{ t('brainComponents.simple.actionButton') }} ({{ props.productName }} - Tarif: {{ props.productId || 'N/A' }})
        </v-btn>
      </div>

      <v-alert
        v-if="errorText"
        type="error"
        variant="tonal"
        class="mt-4"
      >
        {{ errorText }}
      </v-alert>

      <div
        v-if="responseText"
        class="response-block"
      >
        <div class="response-label">
          <v-icon size="18" color="#4caf50" class="mr-2">mdi-check-circle</v-icon>
          {{ t('brainComponents.simple.responseTitle') }}
        </div>
        <p class="response-text">{{ responseText }}</p>
      </div>
    </v-card-text>
    <div
      v-if="isSending || isSearchingAsync"
      class="brain-simple-overlay"
    >
      <div class="overlay-content">
        <v-progress-circular
          indeterminate
          color="#EA5545"
          size="64"
        ></v-progress-circular>
        <p class="overlay-text">
          {{ isSearchingAsync ? 'Clearment.AI Suche läuft... Bitte warten.' : t('brainComponents.common.overlayText') }}
        </p>
      </div>
    </div>
  </v-card>
</template>

<script setup>
import { ref, computed, inject, watch, onBeforeUnmount } from 'vue'
import { useLanguage } from '@/composables/useLanguage'

/* global defineProps, defineEmits */

const props = defineProps({
  productId: {
    type: [Number, String],
    required: true
  },
  productName: {
    type: String,
    default: 'Unknown Product'
  }
})

const emit = defineEmits(['search-completed'])

const baseCalls = inject('baseCalls')
const { t } = useLanguage()

const question = ref('')
const responseText = ref('')
const errorText = ref('')
const isSending = ref(false)
const isSearchingAsync = ref(false)
const asyncStatus = ref(null)
const pollingInterval = ref(null)
const showAsyncButton = ref(true)

const hasProduct = computed(() => props.productId !== null && props.productId !== undefined && props.productId !== '')
const canSubmit = computed(
  () => hasProduct.value && question.value.trim().length > 5 && !isSending.value && !isSearchingAsync.value
)

watch(
  () => props.productId,
  () => {
    responseText.value = ''
    errorText.value = ''
    question.value = ''
  }
)

async function sendQuestion() {
  if (!canSubmit.value) return

  isSending.value = true
  responseText.value = ''
  errorText.value = ''
  try {
    const tarifId = Number(props.productId)
    console.log('=== BrainSimple Debug ===')
    console.log('productId prop:', props.productId)
    console.log('productName prop:', props.productName)
    console.log('tarifId:', tarifId)
    
    const response = await baseCalls.clearmentPost({
      action: 'searchOpenaiVectorStoreTarife',
      prompt: question.value,
      tarif_id: tarifId,
      product_id: 0, // Not needed anymore, using tarif_id directly
      product_name: props.productName
    })

    if (response.data?.success) {
      responseText.value = response.data.result || response.data.payload || ''
      emit('search-completed')
    } else {
      errorText.value = response.data?.error || t('brainComponents.simple.errors.generic')
    }
  } catch (error) {
    console.error('BrainSimple sendQuestion', error)
    errorText.value = t('brainComponents.simple.errors.network')
  } finally {
    isSending.value = false
  }
}

async function sendQuestionAsync() {
  if (!canSubmit.value) return

  isSearchingAsync.value = true
  responseText.value = ''
  errorText.value = ''
  asyncStatus.value = null
  
  // Clear any existing polling
  if (pollingInterval.value) {
    if (typeof pollingInterval.value === 'number') {
      clearTimeout(pollingInterval.value)
    }
    pollingInterval.value = null
  }
  
  try {
    const tarifId = Number(props.productId)
    console.log('=== BrainSimple Async Debug ===')
    console.log('productId prop:', props.productId)
    console.log('productName prop:', props.productName)
    console.log('tarifId:', tarifId)
    
    const response = await baseCalls.clearmentPost({
      action: 'searchOpenaiVectorStoreTarifeAsync',
      prompt: question.value,
      tarif_id: tarifId,
      product_id: 0,
      product_name: props.productName
    })
    
    if (response.data.success && response.data.run_id && response.data.thread_id) {
      asyncStatus.value = {
        status: response.data.status,
        run_id: response.data.run_id,
        thread_id: response.data.thread_id,
        request_id: response.data.request_id
      }
      
      // Start polling for status
      startPolling(response.data.run_id, response.data.thread_id)
      
    } else {
      errorText.value = response.data.error || 'Async-Suche fehlgeschlagen'
      isSearchingAsync.value = false
    }
  } catch (error) {
    console.error('BrainSimple sendQuestionAsync', error)
    errorText.value = 'Fehler bei Async-Suche'
    isSearchingAsync.value = false
  }
}

function startPolling(runId, threadId) {
  const checkStatus = async () => {
    if (!pollingInterval.value) {
      return
    }
    
    try {
      const statusResponse = await baseCalls.clearmentPost({
        action: 'searchOpenaiVectorStoreTarifeStatus',
        run_id: runId,
        thread_id: threadId
      })
      
      const currentStatus = statusResponse.data.status
      const hasResult = statusResponse.data.result && String(statusResponse.data.result).trim().length > 0
      
      if (statusResponse.data.success && hasResult && currentStatus === 'completed') {
        pollingInterval.value = null
        isSearchingAsync.value = false
        
        responseText.value = statusResponse.data.result
        asyncStatus.value = {
          ...asyncStatus.value,
          status: 'completed'
        }
        
        emit('search-completed')
        
        return
      }
      
      if (currentStatus === 'error' || statusResponse.data.error) {
        pollingInterval.value = null
        isSearchingAsync.value = false
        
        errorText.value = statusResponse.data.error || 'Async-Suche fehlgeschlagen'
        return
      }
      
      if (pollingInterval.value) {
        pollingInterval.value = setTimeout(checkStatus, 2000)
      }
      
    } catch (error) {
      console.error('Polling error:', error)
      
      pollingInterval.value = null
      isSearchingAsync.value = false
      
      errorText.value = `Fehler beim Abrufen des Status: ${error.message}`
    }
  }
  
  pollingInterval.value = 'active'
  checkStatus()
}

onBeforeUnmount(() => {
  if (pollingInterval.value) {
    if (typeof pollingInterval.value === 'number') {
      clearTimeout(pollingInterval.value)
    }
    pollingInterval.value = null
  }
})
</script>

<style scoped src="@/assets/styles/sfc/components/objects/BrainSimple.css"></style>
